import { useState, useEffect } from 'react';
  const perms = detectPerms();
import { 
  Plus, 
  Pencil, 
  Trash2, 
  X, 
  Ruler, 
  Search,
  Check,
  AlertCircle,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import { detectPerms } from '../../permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const initialFormData = {
  name: '',
  active: true
};

export const Units = () => {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    fetchUnits();
  }, []);

  const fetchUnits = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/units`, getAuthHeaders());
      setUnits(response.data);
    } catch (err) {
      console.error('Error fetching units:', err);
      setError('Failed to fetch units');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const openAddModal = (prefillName = '') => {
    setFormData({ ...initialFormData, name: prefillName });
    setEditingUnit(null);
    setError('');
    setShowModal(true);
  };

  const openEditModal = (unit) => {
    setFormData({
      name: unit.name || '',
      active: unit.active ?? true
    });
    setEditingUnit(unit);
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.name.trim()) {
      setError('Unit name is required');
      return;
    }

    setSubmitting(true);
    try {
      if (editingUnit) {
        await axios.put(
          `${BACKEND_URL}/api/units/${editingUnit.id}`,
          formData,
          getAuthHeaders()
        );
      } else {
        await axios.post(
          `${BACKEND_URL}/api/units`,
          formData,
          getAuthHeaders()
        );
      }
      setShowModal(false);
      fetchUnits();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save unit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (unitId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/units/${unitId}`, getAuthHeaders());
      setDeleteConfirm(null);
      fetchUnits();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete unit');
    }
  };

  const filteredUnits = units.filter(u =>
    u.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div data-testid="units-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Units of Measurement</h1>
          <p className="text-slate-600 mt-1">Manage measurement units</p>
        </div>
        {perms.canAdd && (
        <button
          onClick={() => openAddModal()}
          data-testid="add-unit-btn"
          className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Add Unit
        </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search units..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-input"
            className="w-full pl-10 pr-10 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none transition-colors"
          />
          {searchQuery.trim() && perms.canAdd && (
            <button onClick={() => { openAddModal(searchQuery.trim()); setSearchQuery(''); }}
              data-testid="search-add-btn" title="Create unit with this name"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-orange-500 hover:text-orange-600 hover:bg-orange-50 rounded transition-colors">
              <Plus className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Units Table */}
      <div className="bg-white rounded-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        ) : filteredUnits.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <Ruler className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No units yet</h3>
            <p className="text-slate-500 mb-6">Start by adding your first unit</p>
            <button
              onClick={() => openAddModal()}
              data-testid="empty-add-btn"
              className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors"
            >
              <Plus className="h-5 w-5" />
              Add First Unit
            </button>
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div className="hidden md:grid grid-cols-3 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Name</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Status</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Actions</div>
            </div>

            {/* Table Rows */}
            {filteredUnits.map((unit) => (
              <div
                key={unit.id}
                data-testid={`unit-row-${unit.id}`}
                className="grid grid-cols-1 md:grid-cols-3 gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 items-center"
              >
                <div className="font-medium text-slate-900">{unit.name}</div>
                <div>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                    unit.active 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {unit.active ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                    {unit.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {perms.canEdit && (
                  <button
                    onClick={() => openEditModal(unit)}
                    data-testid={`edit-btn-${unit.id}`}
                    className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors"
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  )}
                  <button
                    onClick={() => setDeleteConfirm(unit.id)}
                    data-testid={`delete-btn-${unit.id}`}
                    className="p-2 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Confirm Delete</h3>
            <p className="text-slate-600 mb-6">Are you sure you want to delete this unit? This action cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                data-testid="confirm-delete-btn"
                className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-lg my-8">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">
                {editingUnit ? 'Edit Unit' : 'Add New Unit'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6">
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
                  <AlertCircle className="h-5 w-5" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g., Pcs, Kg, Ltr, Box"
                    data-testid="input-name"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                    required
                  />
                </div>

                {/* Active Status */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="active"
                    name="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                    data-testid="input-active"
                    className="w-5 h-5 text-orange-500 border-slate-300 rounded focus:ring-orange-500"
                  />
                  <label htmlFor="active" className="text-sm font-medium text-slate-700">
                    Active
                  </label>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex gap-3 justify-end pt-6 mt-6 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-btn"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editingUnit ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
